import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type ContactRequestStatus = "requested" | "approved" | "declined" | "waiting_student" | "contact_started" | "closed";
export type ContactType = "vaga" | "entrevista_exploratoria" | "mentoria" | "networking" | "proposta";
export type Urgency = "baixa" | "media" | "alta";

export function useContactRequests(orgId?: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["contact_requests", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_requests")
        .select("*, profiles!contact_requests_user_id_fkey(full_name, objective)")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createRequest = useMutation({
    mutationFn: async ({
      userId,
      message,
      jobId,
      contactType,
      urgency,
    }: {
      userId: string;
      message?: string;
      jobId?: string;
      contactType?: ContactType;
      urgency?: Urgency;
    }) => {
      if (!user || !orgId) throw new Error("Missing context");
      const { data, error } = await supabase
        .from("contact_requests")
        .insert({
          org_id: orgId,
          requested_by: user.id,
          user_id: userId,
          message: message || null,
          job_id: jobId || null,
          status: "requested",
          contact_type: contactType || "vaga",
          urgency: urgency || "media",
        })
        .select()
        .single();
      if (error) throw error;

      await supabase.from("partner_audit_logs").insert({
        org_id: orgId,
        actor_user_id: user.id,
        action: "request_contact",
        entity_type: "profile",
        entity_id: userId,
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact_requests", orgId] });
    },
  });

  return {
    requests: query.data ?? [],
    isLoading: query.isLoading,
    createRequest,
  };
}

/** Hook for student to view and respond to contact requests targeting them */
export function useStudentContactRequests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["student_contact_requests", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_requests")
        .select("*, partner_orgs(name)")
        .eq("user_id", user!.id)
        .in("status", ["waiting_student", "contact_started"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const respond = useMutation({
    mutationFn: async ({
      requestId,
      decision,
      reason,
    }: {
      requestId: string;
      decision: "accepted" | "rejected";
      reason?: string;
    }) => {
      const newStatus = decision === "accepted" ? "contact_started" : "closed";
      const { error } = await supabase
        .from("contact_requests")
        .update({
          status: newStatus,
          student_decision: decision,
          student_decision_at: new Date().toISOString(),
          student_reason: reason || null,
        })
        .eq("id", requestId);
      if (error) throw error;

      // Notify partner of student's response (non-blocking)
      try {
        await supabase.functions.invoke("notify-contact-request", {
          body: { contactRequestId: requestId, eventType: "student_responded" },
        });
      } catch (e) {
        console.error("Falha ao enviar notificação:", e);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student_contact_requests", user?.id] });
    },
  });

  const pendingCount = (query.data ?? []).filter((r) => r.status === "waiting_student").length;

  return {
    requests: query.data ?? [],
    pendingCount,
    isLoading: query.isLoading,
    respond,
  };
}

/** Hook for admin to manage all contact requests */
export function useAdminContactRequests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["admin_contact_requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_requests")
        .select("*, partner_orgs(name), profiles!contact_requests_user_id_fkey(full_name, objective, city, state)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      adminNote,
    }: {
      id: string;
      status: string;
      adminNote?: string;
    }) => {
      const { error } = await supabase
        .from("contact_requests")
        .update({
          status,
          admin_note: adminNote || null,
          reviewed_by: user?.id ?? "",
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;

      // Notify student when admin approves (non-blocking)
      if (status === "waiting_student") {
        try {
          await supabase.functions.invoke("notify-contact-request", {
            body: { contactRequestId: id, eventType: "new_request" },
          });
        } catch (e) {
          console.error("Falha ao enviar notificação:", e);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_contact_requests"] });
    },
  });

  return {
    requests: query.data ?? [],
    isLoading: query.isLoading,
    updateStatus,
  };
}
